import { useState } from 'react';
import { useTrackingProjectDashboard } from '../hooks/useAnalyticsDashboard';
import LineChart from '../components/analytics/LineChart';
import ProjectContextBadge from '../components/layout/ProjectContextBadge';
import BookmarkletButton from '../components/BookmarkletButton';

const PERIODS = [
  { value: 14, label: '2 Wochen' },
  { value: 28, label: '4 Wochen' },
  { value: 56, label: '2 Monate' },
];

function Kpi({ label, value, sub }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-4">
      <div className="text-[10.5px] font-semibold tracking-wider text-gray-400 uppercase">{label}</div>
      <div className="text-[24px] font-bold mt-1.5 text-teal">{value ?? '—'}</div>
      {sub && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function Section({ title, children, subtitle }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-[13.5px] font-semibold text-gray-800">{title}</h3>
        {subtitle && <span className="text-[10.5px] text-gray-400">{subtitle}</span>}
      </div>
      {children}
    </div>
  );
}

function TopList({ rows, labelKey, valueKey, valueFormat = (v) => v, accent = '#0C9488' }) {
  if (!rows || rows.length === 0) {
    return <div className="text-[11.5px] text-gray-400 py-3 text-center">Keine Daten.</div>;
  }
  const max = Math.max(...rows.map((r) => Number(r[valueKey]) || 0), 1);
  return (
    <div className="space-y-2">
      {rows.map((r, i) => {
        const v = Number(r[valueKey]) || 0;
        const pct = (v / max) * 100;
        const label = typeof labelKey === 'function' ? labelKey(r) : r[labelKey];
        return (
          <div key={`${label}-${i}`}>
            <div className="flex items-baseline justify-between text-[11.5px] mb-0.5">
              <span className="text-gray-700 font-medium truncate max-w-[70%]">{label || '—'}</span>
              <span className="text-gray-500 tabular-nums">{valueFormat(v)}</span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: accent }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function formatDuration(ms) {
  if (!ms) return '0s';
  const s = Math.round(ms / 1000);
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return m > 0 ? `${m}m ${rem}s` : `${s}s`;
}

export default function AnalyticsTracking() {
  const [days, setDays] = useState(28);
  const { data, isLoading, error } = useTrackingProjectDashboard(days);

  const errorCode = error?.response?.data?.error;

  return (
    <div>
      <div className="flex items-start justify-between mb-5 gap-4">
        <div>
          <ProjectContextBadge />
          <h1 className="text-[23px] font-bold text-gray-900">OptimusAI Tracking</h1>
          <p className="text-[13px] text-gray-500 mt-0.5">
            Eigenes Tracking via ko-tracker.js — Scroll-Tiefe, Klick-Events &amp; Conversions
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data?.project?.tracking_code && (
            <BookmarkletButton
              trackingCode={data.project.tracking_code}
              contactLabel={data.project.contact_company || data.project.contact_name}
              variant="compact"
            />
          )}
          <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-lg p-1">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                onClick={() => setDays(p.value)}
                className={`px-3 py-1.5 rounded-md text-[12px] font-medium transition-colors ${
                  days === p.value ? 'bg-teal-light text-teal' : 'text-gray-500 hover:bg-gray-50'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {isLoading && (
        <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-12 text-center text-[13px] text-gray-400">
          Lade Tracking-Daten…
        </div>
      )}

      {error && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-[12.5px] text-amber-800">
          <div className="font-semibold mb-1">Tracking-Daten nicht verfügbar</div>
          <div>{error.response?.data?.message || error.message}</div>
          {(errorCode === 'no_contact' || errorCode === 'tracking_disabled') && (
            <a href="/" className="inline-block mt-2 text-teal font-medium hover:underline">
              → Zur Projekt-Übersicht
            </a>
          )}
        </div>
      )}

      {data && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5">
            <Kpi label="Pageviews" value={data.summary.pageviews?.toLocaleString('de')} />
            <Kpi label="Sessions" value={data.summary.sessions?.toLocaleString('de')} />
            <Kpi label="Conversions" value={data.summary.conversions?.toLocaleString('de')} />
            <Kpi label="Conv.-Rate" value={`${data.summary.conversion_rate ?? 0}%`} />
            <Kpi label="Ø Scroll-Tiefe" value={`${Math.round(data.summary.avg_scroll_depth ?? 0)}%`} />
            <Kpi label="Ø Verweildauer" value={formatDuration(data.summary.avg_duration_ms)} />
          </div>

          {/* Linechart: Pageviews + Sessions */}
          <Section title="Pageviews & Sessions — Verlauf">
            <LineChart
              series={[
                {
                  name: 'Pageviews',
                  color: '#0C9488',
                  points: (data.pageviews_by_day || []).map((d) => ({ date: d.date, value: d.count })),
                },
                {
                  name: 'Sessions',
                  color: '#1C2E3C',
                  points: (data.sessions_by_day || []).map((d) => ({ date: d.date, value: d.count })),
                },
              ]}
            />
          </Section>

          {/* Linechart: Scroll-Tiefe (unser Alleinstellungsmerkmal) */}
          <div className="mt-3">
            <Section title="Ø Scroll-Tiefe — Verlauf" subtitle="Unser USP vs. GA4">
              <LineChart
                series={[
                  {
                    name: 'Scroll-Tiefe (%)',
                    color: '#F59E0B',
                    points: (data.scroll_by_day || []).map((d) => ({
                      date: d.date,
                      value: Math.round(Number(d.avg_depth) || 0),
                    })),
                  },
                ]}
                format={(v) => `${v}%`}
              />
            </Section>
          </div>

          {/* Grids */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
            <Section title="Top-Seiten">
              <TopList
                rows={data.top_pages || []}
                labelKey={(r) => r.page_title || r.page_url}
                valueKey="views"
                valueFormat={(v) => `${v.toLocaleString('de')} Views`}
              />
            </Section>
            <Section title="Conversions nach Label">
              <TopList
                rows={data.conversion_labels || []}
                labelKey="conversion_label"
                valueKey="count"
                valueFormat={(v) => `${v.toLocaleString('de')}`}
              />
            </Section>
            <Section title="Devices">
              <TopList
                rows={data.devices || []}
                labelKey="device_type"
                valueKey="count"
                valueFormat={(v) => v.toLocaleString('de')}
              />
            </Section>
            <Section title="Browser">
              <TopList
                rows={data.browsers || []}
                labelKey="browser"
                valueKey="count"
                valueFormat={(v) => v.toLocaleString('de')}
              />
            </Section>
            <Section title="UTM-Quellen">
              <TopList
                rows={data.utm_sources || []}
                labelKey={(r) => `${r.utm_source || '—'}${r.utm_medium ? ` / ${r.utm_medium}` : ''}`}
                valueKey="count"
                valueFormat={(v) => v.toLocaleString('de')}
              />
            </Section>
          </div>
        </>
      )}
    </div>
  );
}
